import { mapState } from 'vuex';
import { Options, Vue } from 'vue-class-component';
import {
  AddressingType,
  ApartmentDTO,
  BuildingCreateDTO,
  EntrancePlantDetailDTO,
  EntrancePlantStructureCreateDTO,
  FloorDTO,
} from '@/../client-generator/generated-clients/plants';
import ApartmentItem from './ApartmentItem/ApartmentItem.vue';
import DialogAddEditBuilding from '../DialogAddEditBuilding/DialogAddEditBuilding.vue';
import DialogAddEditApartment from './DialogAddEditApartment/DialogAddEditApartment.vue';
import plantsClient from '@/core/services/plants';
import { SET_APP_LOADING } from '@/core/store/action-types';
import DialogConfirmDelete from '@/views/components/DialogConfirmDelete/DialogConfirmDelete.vue';
import { loadRisersFloorsApartments } from '@/core/utils/EntrancePlant/entrance-plant.utils';
import { NamingService } from '@/core/services/naming.service';
import debounce from 'lodash.debounce';
import { NAME_VALIDATION_DEBOUNCE_TIME } from '@/core/utils/utils';

@Options({
  name: 'EntrancePlantStructureBuilding',
  computed: {
    ...mapState(['currentEntrancePlant']),
  },
  components: {
    ApartmentItem,
    DialogAddEditBuilding,
    DialogAddEditApartment,
    DialogConfirmDelete,
  },
  emits: ['update-list'],
})
export default class EntrancePlantStructureBuilding extends Vue {
  currentEntrancePlant!: EntrancePlantDetailDTO;

  isVisibleDeleteBuildingConfirm = false;
  apartmentToEdit: ApartmentDTO | null = null;
  isVisibleAddEditApartment = false;
  isVisibleAddEditBuilding = false;
  isValidName = true;

  AddressingType = AddressingType;

  async created() {
    this.verifyValidityOfName = debounce(
      this.verifyValidityOfName,
      NAME_VALIDATION_DEBOUNCE_TIME + 1000
    );
  }

  mounted() {}

  get backLink() {
    return {
      name: 'page-entranceplant-settings',
      params: this.$route.params,
    };
  }

  get building() {
    return this.currentEntrancePlant.buildings!.find(
      (plantBuilding) =>
        plantBuilding.buildingId === this.$route.params.buildingId.toString()
    );
  }

  get apartments() {
    return this.building?.apartments;
  }

  get buildingStructure() {
    if (this.building?.risers === undefined) return [];
    return loadRisersFloorsApartments(this.building);
  }

  async verifyValidityOfName(newName: string, floor: FloorDTO) {
    if (newName === floor.name) return;
    const validName = NamingService.isValidEntityTypeName(newName);

    if (!validName) {
      this.isValidName = validName;
    } else {
      this.isValidName = true;
      await this.updateFloorName(newName, floor);
    }
  }

  async updateFloorName(newName: string, floor: FloorDTO) {
    if (this.currentEntrancePlant.desktopId === undefined) return;
    try {
      this.$store.dispatch(SET_APP_LOADING, true);
      floor.name = newName;
      await plantsClient.floor(this.currentEntrancePlant.desktopId, floor)
      this.$toast.add({
        severity: 'success',
        summary: this.$t('entrancePlant.floorEditedBanner'),
        life: 3000,
      });
      NamingService.forcefullyRenameFirstEntityWithinCachedOnes(
        floor.name!,
        newName
      );
    } catch (e) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('entrancePlant.floorInlineNamingError'),
        life: 3000,
      });
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
      this.$emitter.emit('update-list');
    }
  }

  toggleAddEditBuilding() {
    this.isVisibleAddEditBuilding = !this.isVisibleAddEditBuilding;
  }

  toggleAddApartment() {
    this.apartmentToEdit = null;
    this.isVisibleAddEditApartment = true;
  }

  toggleEditApartment(apartment: ApartmentDTO) {
    this.apartmentToEdit = apartment;
    this.isVisibleAddEditApartment = true;
  }

  hideAddEditApartment() {
    this.isVisibleAddEditApartment = false;
  }

  toggleDeleteBuildingConfirm() {
    this.isVisibleDeleteBuildingConfirm = !this.isVisibleDeleteBuildingConfirm;
  }

  handleUpdateList() {
    this.$emit('update-list');
  }

  async handleDeleteBuilding() {
    if (this.currentEntrancePlant.desktopId === undefined) return;
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      const structure = new EntrancePlantStructureCreateDTO();
      structure.buildingsCreate = [];
      this.currentEntrancePlant.buildings?.forEach((epBuilding) =>
        structure.buildingsCreate?.push(
          new BuildingCreateDTO({
            buildingId: epBuilding.buildingId,
            name: epBuilding.name,
            apartmentsCount: epBuilding.apartments?.length,
          })
        )
      );
      const editingIndex = structure.buildingsCreate.findIndex(
        (editing) => editing.buildingId === this.building?.buildingId
      );
      structure.buildingsCreate.splice(editingIndex, 1);

      await plantsClient.structure(
        this.currentEntrancePlant.desktopId,
        structure
      );

      this.$router.push(this.backLink);
      this.$emit('update-list');
      setTimeout(() => {
        this.$toast.add({
          severity: 'success',
          summary: this.$t('AppResourceEP.BuildingBannerDeleted'),
          closable: false,
          life: 3000,
        });
      }, 1500);
    } catch {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }
}
